'use client'

/**
 * §19.2 — the activity rail. Resolves §16's AMENDED "no toasts" refusal:
 * a persistent, scrollable, append-only record instead of a message that
 * vanishes. Newest first. Nothing auto-dismisses. Nothing is lost.
 *
 * Desktop: a persistent aside next to the workspace. Mobile: a sheet,
 * opened from the always-visible status row that mirrors the newest line.
 *
 * DOCUMENTED EXCEPTION to §19.2's "the rail is the editor's ONE live region
 * (section role=log)". The rail is rendered as role="list", deliberately,
 * and this is the ruling rather than a gap:
 *
 * role="log" carries an IMPLICIT aria-live="polite" per the ARIA spec —
 * confirmed against Chrome's accessibility tree, where every role="log"
 * node reports {"live":"polite"} whether or not aria-live is written. With
 * the rail as a log, every "homeless" entry (redo discarded, export failed,
 * generation failed) was spoken twice in the same tick: once by the rail,
 * once by lib/announce.ts's assertive channel, and the assertive one cut
 * the polite one off mid-read. §11.1 designates lib/announce.ts the site's
 * single live-region writer, so the resolution is that the editor has NO
 * live region of its own: the rail is a navigable role="list" (no implicit
 * live behaviour), the compatibility verdict (§19.8) goes through
 * announce() from compatibility-indicator.tsx, and session-log.ts routes
 * every homeless entry through announce() too. A scan of `main` for live
 * regions on /convert therefore returns none by design — the two shell
 * nodes live outside `main`, in app/layout.tsx. The spec text at §19.2 and
 * §19.8 should read this way; see HANDOFF.md for the amendment.
 */

import { useId } from 'react'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetTrigger,
} from '@/components/ui/sheet'
import type { SessionLogEntry } from './session-log'
import { cn } from '@/lib/utils'

// HH:MM in the 24-hour cycle: five tabular characters that fit the 52px
// time column at every locale. The 12-hour form ("08:22 PM") wrapped onto a
// second line on every entry.
function formatTime(t: number): string {
  const d = new Date(t)
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' })
}

function RailList({ entries }: { entries: SessionLogEntry[] }) {
  const listId = useId()
  if (entries.length === 0) {
    return (
      <p className="py-4 text-sm text-[var(--tls-fg-subtle)]">
        Nothing yet. Actions in this session will appear here as you go.
      </p>
    )
  }
  return (
    <ol id={listId} role="list" aria-label="Activity, this session" className="flex flex-col">
      {entries.map((entry) => (
        <li
          key={entry.id}
          className="grid grid-cols-[52px_1fr] items-baseline gap-3 border-b border-[color:var(--tls-border-subtle)] py-2.5 text-sm last:border-b-0"
        >
          <span className="tlsr-mono text-sm text-[var(--tls-fg-subtle)]">{formatTime(entry.t)}</span>
          <span
            className={cn(
              'leading-snug',
              entry.kind === 'generation' && entry.label.toLowerCase().includes('fail')
                ? 'font-semibold text-[var(--tls-fg-strong)]'
                : 'text-[var(--tls-fg-muted)]'
            )}
          >
            {entry.label}
          </span>
        </li>
      ))}
    </ol>
  )
}

interface ActivityRailProps {
  log: SessionLogEntry[]
  className?: string
}

/** Persistent desktop aside — always mounted, scrolls internally. */
export function ActivityRailDesktop({ log, className }: ActivityRailProps) {
  const newestFirst = [...log].reverse()
  return (
    // Deliberately not a bordered/filled box (Rule 1 — no card): a left
    // hairline only, matching every other flat surface in the workspace.
    <div
      className={cn(
        'hidden lg:flex lg:w-[340px] lg:shrink-0 lg:flex-col',
        'border-l border-[color:var(--tls-border-subtle)] pl-5',
        className
      )}
    >
      <p className="tls-h4 border-b border-[color:var(--tls-border-subtle)] pb-3">
        Activity
      </p>
      <div className="max-h-[520px] overflow-y-auto">
        <RailList entries={newestFirst} />
      </div>
    </div>
  )
}

/**
 * Status row + mobile sheet. The status row always shows the newest line
 * and is never the only place a fact appears — it mirrors the rail, and
 * the rail is permanent (§19.2). On mobile it docks above the export
 * button at a 44px minimum target.
 */
export function ActivityStatusRow({ log }: { log: SessionLogEntry[] }) {
  const newest = log[log.length - 1] ?? null
  return (
    <div className="flex min-h-[44px] items-center gap-3 border-t border-[color:var(--tls-border-strong)] py-2.5 lg:hidden">
      <p className="min-w-0 flex-1 truncate text-sm font-medium text-[var(--tls-fg-strong)]">
        {newest ? newest.label : 'No activity yet'}
      </p>
      <Sheet>
        <SheetTrigger asChild>
          <button
            type="button"
            className="tlsr-focus flex min-h-[44px] shrink-0 items-center gap-1 px-2 tls-eyebrow [--tls-eyebrow-color:var(--tls-fg-strong)]"
          >
            Activity ▲
          </button>
        </SheetTrigger>
        <SheetContent side="bottom" className="max-h-[80vh]">
          <SheetHeader>
            <SheetTitle>Activity</SheetTitle>
            <SheetDescription>
              Everything you did in this session, newest first.
            </SheetDescription>
          </SheetHeader>
          <div className="overflow-y-auto px-4 pb-4">
            <RailList entries={[...log].reverse()} />
          </div>
        </SheetContent>
      </Sheet>
    </div>
  )
}
